/**
 * Datart
 *
 * Copyright 2021
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {PayloadAction} from '@reduxjs/toolkit';
import {
  Banner,
  BoardLinkFilter,
  ContainerWidgetContent,
  ControllerWidgetContent,
  Dashboard,
  DataChart, QuickQueryConfig,
  WidgetData,
  WidgetInfo,
} from 'app/pages/DashBoardPage/pages/Board/slice/types';
import ChartDataView from 'app/types/ChartDataView';
import { useInjectReducer } from 'utils/@reduxjs/injectReducer';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { PageInfo } from '../../../../MainPage/pages/ViewPage/slice/types';
import { createWidgetInfo, getCascadeControllersDeep } from '../../../utils/widget';
// import { widgetsQueryAction, widgetsQueryItemAction } from './asyncActions';
import {getChartWidgetDataAsync, getControllerOptions, getUserDashboardQueryConfigs, getVizBanners} from './thunk';
import {BoardInfo, BoardState, Widget} from './types';

export const boardInit: BoardState = {
  boardRecord: {} as Record<string, Dashboard>,
  boardInfoRecord: {} as Record<string, BoardInfo>,
  widgetRecord: {} as Record<string, Record<string, Widget>>,
  widgetInfoRecord: {} as Record<string, Record<string, WidgetInfo>>,
  widgetDataMap: {} as Record<string, WidgetData>,
  dataChartMap: {} as Record<string, DataChart>,
  viewMap: {} as Record<string, ChartDataView>, // View
  banner: {} as Record<string, Banner>,
  loading: {} as Record<string, number>,
  queryTimestamp: {} as Record<string, number>,
  // tabPanel: {} as Record<string, { activeKeys: [], loadkeys: [], }>,
};
// boardActions
const boardSlice = createSlice({
  name: 'board',
  initialState: boardInit as BoardState,
  reducers: {
    setBoardState(
      state,
      action: PayloadAction<{
        board: Dashboard;
        boardInfo: BoardInfo;
      }>,
    ) {
      const { board, boardInfo } = action.payload;
      state.boardRecord[board.id] = board;
      state.boardInfoRecord[board.id] = boardInfo;
      // can not del :dataCharts、views
    },
    setWidgetMapState(
      state,
      action: PayloadAction<{
        boardId: string;
        widgetMap: Record<string, Widget>;
        widgetInfoMap: Record<string, WidgetInfo>;
      }>,
    ) {
      const { boardId, widgetMap, widgetInfoMap } = action.payload;
      if (!state.widgetRecord[boardId]) {
        state.widgetRecord[boardId] = {};
      }
      state.widgetRecord[boardId] = widgetMap;
      if (!state.widgetInfoRecord[boardId]) {
        state.widgetInfoRecord[boardId] = {};
      }
      state.widgetInfoRecord[boardId] = widgetInfoMap;
    },
    setDataChartToMap(state, action: PayloadAction<DataChart[]>) {
      const dataCharts = action.payload;
      dataCharts.forEach(dc => {
        state.dataChartMap[dc.id] = dc;
      });
    },
  
    setLoadingMap(
      state,
      action: PayloadAction<{
        boardId: string;
        count: number
      }>,
    ) {
      const { boardId, count } = action.payload;
      state.loading[boardId] = (state.loading[boardId] || 0) + count;
    },
    setQueryTimestampMap(
      state,
      action: PayloadAction<{
        boardId: string;
      }>,
    ) {
      const { boardId } = action.payload;
      console.log("state.queryTimestamp", state.queryTimestamp)
      state.queryTimestamp[boardId] = Date.now();
    },

    setViewMap(state, action: PayloadAction<ChartDataView[]>) {
      const views = action.payload;
      views.forEach(view => {
        state.viewMap[view.id] = view;
      });
    },

    clearBoardStateById(state, action: PayloadAction<string>) {
      const boardId = action.payload;
      delete state.boardRecord[boardId];
      delete state.boardInfoRecord[boardId];
      delete state.widgetRecord[boardId];
      delete state.widgetInfoRecord[boardId];
      // can not del :dataCharts、views
    },

    clearBoardDataById(state, action: PayloadAction<string>) {
      const boardId = action.payload;
      if (state.widgetRecord[boardId]) {
        Object.keys(state.widgetRecord[boardId]).map(key => {
          state.widgetDataMap[key] = {
            id: key,
            columns: [],
            rows: [],
          }
        })
      }
    },

    clearControllerWidgetRelationControllerValue(state, action: PayloadAction<{ boardId: string, widgetId: string }>) {
      const {boardId, widgetId} = action.payload;
      if (state.widgetRecord[boardId]) {
        let ids = [] as string[];
        ids = getCascadeControllersDeep([widgetId], state.widgetRecord[boardId], ids);
        console.log("关联的widget controller ids:", boardId, widgetId, ids);

        Object.keys(state.widgetRecord[boardId]).map(key => {
          if (ids.includes(key)) {
            const controllerWidgetContent = state.widgetRecord[boardId][key].config.content as ControllerWidgetContent;
            controllerWidgetContent.config.controllerValues = [];
          }
        })
      }
    },

    setControllerWidgetValue(state, action: PayloadAction<{ boardId: string, config: QuickQueryConfig[] }>) {
      const { boardId, config } = action.payload;
      if (state.widgetRecord[boardId]) {

        config.map(content => {
          if (state.widgetRecord[boardId][content.controllerWidgetId]) {
            const controllerWidgetContent = state.widgetRecord[boardId][content.controllerWidgetId].config.content as ControllerWidgetContent;
            if (controllerWidgetContent.type === 'rangeTime' || controllerWidgetContent.type === 'rangeTimePicker') {
              controllerWidgetContent.config.controllerDate = content.controllerDate;
            } else {
              controllerWidgetContent.config.controllerValues = content.controllerValues;
            }
          }
        });
      }
    },

    setGroupWidgetsById(
      state,
      action: PayloadAction<{ boardId: string; widgets: Widget[] }>,
    ) {
      const { boardId: groupId, widgets } = action.payload;
      state.widgetRecord[groupId] = widgets.reduce((obj, widget) => {
        obj[widget.id] = widget;
        return obj;
      }, {});

      state.widgetInfoRecord[groupId] = widgets.reduce((obj, widget) => {
        obj[widget.id] = createWidgetInfo(widget.id);
        return obj;
      }, {});
    },

    updateWidget(state, action: PayloadAction<Widget>) {
      const widget = action.payload;
      state.widgetRecord[widget.dashboardId][widget.id] = widget;
    },

    updateViewMap(state, action: PayloadAction<ChartDataView[]>) {
      const views = action.payload;
      views.forEach(view => {
        state.viewMap[view.id] = view;
      });
    },

    renderedWidgets(
      state,
      action: PayloadAction<{ boardId: string; widgetIds: string[] }>,
    ) {
      const { boardId: groupId, widgetIds } = action.payload;

      widgetIds.forEach(wid => {
        try {
          state.widgetInfoRecord[groupId][wid].rendered = true;
        } catch (error) {}
      });
    },
    updateFullScreenPanel(
      state,
      action: PayloadAction<{ boardId: string; itemId: string }>,
    ) {
      const { boardId, itemId } = action.payload;
      state.boardInfoRecord[boardId].fullScreenItemId = itemId;
    },

    setWidgetData(state, action: PayloadAction<WidgetData>) {
      const widgetData = action.payload;
      state.widgetDataMap[widgetData.id] = widgetData;
    },
    changeBoardVisible(
      state,
      action: PayloadAction<{ id: string; visible: boolean }>,
    ) {
      const { id, visible } = action.payload;
      if (!id || !state.boardInfoRecord[id]) return;
      state.boardInfoRecord[id].visible = visible;
    },
    changeBoardLinkFilter(
      state,
      action: PayloadAction<{
        boardId: string;
        triggerId: string;
        linkFilters?: BoardLinkFilter[];
      }>,
    ) {
      const { boardId, triggerId, linkFilters } = action.payload;
      if (!boardId) return;
      state.boardInfoRecord[boardId].linkFilter = state.boardInfoRecord[
        boardId
      ].linkFilter.filter(link => link.triggerWidgetId !== triggerId);
      if (linkFilters) {
        state.boardInfoRecord[boardId].linkFilter =
          state.boardInfoRecord[boardId].linkFilter.concat(linkFilters);
      }
    },
    changeWidgetInLinking(
      state,
      action: PayloadAction<{
        boardId: string;
        widgetId: string;
        toggle: boolean;
      }>,
    ) {
      const { boardId, widgetId, toggle } = action.payload;
      if (!toggle) {
        state.boardInfoRecord[boardId].linkFilter = state.boardInfoRecord[
          boardId
        ].linkFilter.filter(link => link.triggerWidgetId !== widgetId);
      }
      state.widgetInfoRecord[boardId][widgetId].inLinking = toggle;
    },

    addFetchedItem(
      state,
      action: PayloadAction<{ boardId: string; widgetId: string }>,
    ) {
      const { boardId, widgetId } = action.payload;
      try {
        const hasItems = state.boardInfoRecord?.[boardId].hasFetchItems;
        state.boardInfoRecord[boardId].hasFetchItems = Array.from(
          new Set([...hasItems, widgetId]),
        );
      } catch (error) {}
    },

    setBoardWidthHeight(
      state,
      action: PayloadAction<{ boardId: string; wh: [number, number] }>,
    ) {
      const { boardId, wh } = action.payload;
      let boardInfo = state.boardInfoRecord?.[boardId];
      if (boardInfo) {
        state.boardInfoRecord[boardId].boardWidthHeight = wh;
      }
    },
    changeBoardPublish(
      state,
      action: PayloadAction<{ boardId: string; publish: number }>,
    ) {
      const { boardId, publish } = action.payload;
      // 1 发布了， 2 取消发布
      state.boardRecord[boardId].status = publish;
    },
    changePageInfo(
      state,
      action: PayloadAction<{
        boardId: string;
        widgetId: string;
        pageInfo: Partial<PageInfo> | undefined;
      }>,
    ) {
      const { boardId, widgetId, pageInfo } = action.payload;
      if (!state.widgetInfoRecord?.[boardId]?.[widgetId]) return;
      state.widgetInfoRecord[boardId][widgetId].pageInfo = pageInfo || {
        pageNo: 1,
      };
    },
    setWidgetErrInfo(
      state,
      action: PayloadAction<{
        boardId: string;
        widgetId: string;
        errInfo?: string;
        errorType: 'request' | 'interaction';
      }>,
    ) {
      const { boardId, widgetId, errInfo, errorType } = action.payload;

      if (!state.widgetInfoRecord?.[boardId]?.[widgetId]) return;
      let widgetInfo = state.widgetInfoRecord[boardId][widgetId];

      if (errInfo) {
        widgetInfo.errInfo[errorType] = errInfo;
      } else {
        delete widgetInfo.errInfo[errorType];
      }
    },
    resetControlWidgets(
      state,
      action: PayloadAction<{
        boardId: string;
      }>,
    ) {
      const { boardId } = action.payload;
      const originControllerWidgets =
        state.boardInfoRecord[boardId].originControllerWidgets;
      if (originControllerWidgets.length === 0) {
        return;
      }
      originControllerWidgets.forEach(widget => {
        state.widgetRecord[boardId][widget.id] = widget;
      });
    },


    tabsChangeActiveKey(
      state,
      action: PayloadAction<{
        activeKey: string,
        Widget: Widget,
        // isQuery?: boolean,
      }>,
    ) {
      const {Widget, activeKey} = action.payload;
      const tabsContainerConfig = state.widgetRecord[Widget.dashboardId][Widget.id].config
        .content as ContainerWidgetContent;
        
      
      // if(isQuery && tabsContainerConfig.tabConfig.activeKey && state.tabPanel[Widget.dashboardId]) {
      //   // console.log(Widget.dashboardId, state.tabPanel[Widget.dashboardId].activeKeys, tabsContainerConfig.tabConfig.activeKey);
      //   const oldKey = Object.values((Widget.config.content as ContainerWidgetContent).itemMap).find(i => i.tabId === tabsContainerConfig.tabConfig.activeKey)?.childWidgetId;
      //   const newKey = Object.values((Widget.config.content as ContainerWidgetContent).itemMap).find(i => i.tabId === activeKey)?.childWidgetId;
      //   console.log(oldKey, newKey);
      //
      //   const activeKeys = state.tabPanel[Widget.dashboardId].activeKeys.filter(i => i !== oldKey).concat(newKey!);
      //   const loadkeys = [...new Set([...state.tabPanel[Widget.dashboardId].loadkeys, newKey!])];
      //
      //   state.tabPanel[Widget.dashboardId] = {
      //     loadkeys: loadkeys,
      //     activeKeys,
      //   };
      // }
      tabsContainerConfig.tabConfig = {activeKey: activeKey}
    },

    // setTabPanelActiveKey(
    //   state,
    //   action: PayloadAction<{
    //     key: string,
    //     boardId: string,
    //   }>,
    // ) {
    //   const { key, boardId } = action.payload;
    //   state.tabPanel[boardId] = {
    //     loadkeys: [...state.tabPanel[boardId]?.loadkeys || [], key],
    //     activeKeys: [...(state.tabPanel?.[boardId]?.activeKeys || []), key],
    //   };
    // },

    // setTabPanelLoadKey(
    //   state,
    //   action: PayloadAction<{
    //     key?: string,
    //     keys?: string[],
    //     boardId: string,
    //   }>,
    // ) {
    //   const { key, keys, boardId } = action.payload;
    //   if(key) {
    //     state.tabPanel[boardId] = {
    //       ...state.tabPanel[boardId],
    //       loadkeys: [...(state.tabPanel?.[boardId]?.loadkeys || []), key],
    //     };
    //   } else if (keys) {
    //     state.tabPanel[boardId] = {
    //       ...state.tabPanel[boardId],
    //       loadkeys: [...(state.tabPanel?.[boardId]?.loadkeys || []), ...keys],
    //     };
    //   }
    // }

  },
  extraReducers: builder => {
    builder.addCase(getChartWidgetDataAsync.pending, (state, action) => {
      const { boardId, widgetId } = action.meta.arg;
      try {
        state.widgetInfoRecord[boardId][widgetId].loading = true;
      } catch (error) {}
    });
    builder.addCase(getChartWidgetDataAsync.fulfilled, (state, action) => {
      const { boardId, widgetId } = action.meta.arg;
      try {
        state.widgetInfoRecord[boardId][widgetId].loading = false;
      } catch (error) {}
    });
    builder.addCase(getChartWidgetDataAsync.rejected, (state, action) => {
      const { boardId, widgetId } = action.meta.arg;
      try {
        state.widgetInfoRecord[boardId][widgetId].loading = false;
      } catch (error) {}
    });
    builder.addCase(getControllerOptions.pending, (state, action) => {
      const { boardId, widgetId } = action.meta.arg;
      try {
        state.widgetInfoRecord[boardId][widgetId].loading = true;
      } catch (error) {}
    });
    builder.addCase(getControllerOptions.fulfilled, (state, action) => {
      const { boardId, widgetId } = action.meta.arg;
      try {
        state.widgetInfoRecord[boardId][widgetId].loading = false;
      } catch (error) {}
    });
    builder.addCase(getControllerOptions.rejected, (state, action) => {
      const { boardId, widgetId } = action.meta.arg;
      try {
        state.widgetInfoRecord[boardId][widgetId].loading = false;
      } catch (error) {}
    });

    builder.addCase(getVizBanners.fulfilled, (state, action) => {
      const banners = action.payload;
      banners?.forEach(item => {
        state.banner[item.pageKey] = item;
      });
    });

    builder.addCase(getUserDashboardQueryConfigs.fulfilled, (state, action) => {
      const { dashboardId } = action.meta.arg;
      state.boardInfoRecord[dashboardId].quickQuerys = action.payload;
    })
  },
});
export const { actions: boardActions } = boardSlice;
export const useBoardSlice = () => {
  useInjectReducer({ key: 'board', reducer: boardSlice.reducer });
};
